import React, { useEffect, useState } from 'react';
import { dashboardService } from '../services';
import { DashboardStats } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  Building2,
  MapPin,
  Calendar,
  Briefcase,
  Megaphone,
  CheckCircle2,
  Clock,
  Loader2,
} from 'lucide-react';

interface StatCard {
  title: string;
  value: number;
  icon: React.ReactElement;
  description: string;
  trend?: string;
}

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardStats = await dashboardService.getDashboardStats();
      setStats(dashboardStats);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (
    status: string
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'PENDING':
        return 'outline';
      case 'CONFIRMED':
        return 'default';
      case 'CANCELLED':
        return 'destructive';
      case 'COMPLETED':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        <p className="font-medium">Error loading dashboard</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="rounded-lg border border-muted bg-muted/50 p-4">
        <p className="text-muted-foreground">No dashboard data available</p>
      </div>
    );
  }

  const statCards: StatCard[] = stats
    ? [
        {
          title: 'Total Users',
          value: stats.totalUsers || 0,
          icon: <Users className="h-4 w-4 text-muted-foreground" />,
          description: `${stats.pendingUsers || 0} pending approval`,
        },
        {
          title: 'Registration Requests',
          value: stats.totalRegistrationRequests || 0,
          icon: <Clock className="h-4 w-4 text-muted-foreground" />,
          description: `${stats.pendingRegistrationRequests || 0} pending`,
        },
        {
          title: 'Organizations',
          value: stats.totalOrganizations || 0,
          icon: <Building2 className="h-4 w-4 text-muted-foreground" />,
          description: 'Total organizations',
        },
        {
          title: 'Branches',
          value: stats.totalBranches || 0,
          icon: <MapPin className="h-4 w-4 text-muted-foreground" />,
          description: 'Active locations',
        },
        {
          title: 'Operations',
          value: stats.totalOperations || 0,
          icon: <Calendar className="h-4 w-4 text-muted-foreground" />,
          description: 'Total operations',
        },
        {
          title: 'Job Listings',
          value: stats.totalJobs || 0,
          icon: <Briefcase className="h-4 w-4 text-muted-foreground" />,
          description: 'Active jobs',
        },
        {
          title: 'Campaigns',
          value: stats.totalCampaigns || 0,
          icon: <Megaphone className="h-4 w-4 text-muted-foreground" />,
          description: 'Active campaigns',
        },
        {
          title: 'Pending Branch Requests',
          value: stats.pendingBranchRequests || 0,
          icon: <CheckCircle2 className="h-4 w-4 text-muted-foreground" />,
          description: 'Branches awaiting approval',
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to your Turnect admin dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              {card.icon}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Operations */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Operations</CardTitle>
          <CardDescription>Latest operations in the system</CardDescription>
        </CardHeader>
        <CardContent>
          {stats && stats.recentOperations && stats.recentOperations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recentOperations.map((operation) => (
                  <TableRow key={operation.id}>
                    <TableCell className="font-medium">{operation.id}</TableCell>
                    <TableCell>{operation.businessOwner?.fullName || '-'}</TableCell>
                    <TableCell>{operation.branch?.name || '-'}</TableCell>
                    <TableCell>
                      {operation.dateTime ? new Date(operation.dateTime).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(operation.status)}>{operation.status}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(operation.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-sm text-muted-foreground py-8">No recent operations</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
